import { MakeLogicType, actions, kea, key, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'

import { LemonDialog, lemonToast } from '@posthog/lemon-ui'

import api from 'lib/api'
import { createFuse, Fuse } from 'lib/utils/fuseSearch'
import { urls } from 'scenes/urls'

import { deleteFromTree } from '~/layout/panel-layout/ProjectTree/projectTreeLogic'

import type { HogFlow } from './hogflows/types'

export type WorkflowStatusFilter = 'all' | 'active' | 'draft' | 'archived'

export interface WorkflowsFilters {
    search: string
    createdBy: string | null
    status: WorkflowStatusFilter
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicValues {
    allArchivedSelected: boolean
    creators: {
        email: string
        id: number
        name: string
        uuid: string
    }[]
    filteredWorkflows: HogFlow[]
    filters: WorkflowsFilters
    hasLoadedWorkflows: boolean
    selectedArchivedCount: number
    selectedArchivedWorkflowIds: Set<string>
    workflows: HogFlow[]
    workflowsFuse: Fuse<HogFlow>
    workflowsLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicActions {
    archiveWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    archiveWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    archiveWorkflowSuccess: (
        workflows: HogFlow[],
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: HogFlow[]
        payload?: {
            workflow: HogFlow
        }
    }
    clearArchivedWorkflowSelection: () => {
        value: true
    }
    deleteSelectedWorkflows: () => {
        value: true
    }
    deleteWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    deleteWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteWorkflowSuccess: (
        workflows: HogFlow[],
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: HogFlow[]
        payload?: {
            workflow: HogFlow
        }
    }
    duplicateWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    duplicateWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    duplicateWorkflowSuccess: (
        workflows: HogFlow[],
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: HogFlow[]
        payload?: {
            workflow: HogFlow
        }
    }
    loadWorkflows: () => {}
    loadWorkflowsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadWorkflowsSuccess: (
        workflows: HogFlow[],
        payload?: {}
    ) => {
        workflows: HogFlow[]
        payload?: {}
    }
    restoreWorkflow: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    restoreWorkflowFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    restoreWorkflowSuccess: (
        workflows: HogFlow[],
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: HogFlow[]
        payload?: {
            workflow: HogFlow
        }
    }
    selectAllArchivedWorkflows: (ids: string[]) => {
        ids: string[]
    }
    setCreatedBy: (createdBy: string | null) => {
        createdBy: string | null
    }
    setFilters: (filters: Partial<WorkflowsFilters>) => {
        filters: Partial<WorkflowsFilters>
    }
    setSearchTerm: (search: string) => {
        search: string
    }
    setStatusFilter: (status: WorkflowStatusFilter) => {
        status: WorkflowStatusFilter
    }
    toggleArchivedWorkflowSelection: (id: string) => {
        id: string
    }
    toggleWorkflowStatus: (workflow: HogFlow) => {
        workflow: HogFlow
    }
    toggleWorkflowStatusFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    toggleWorkflowStatusSuccess: (
        workflows: HogFlow[],
        payload?: {
            workflow: HogFlow
        }
    ) => {
        workflows: HogFlow[]
        payload?: {
            workflow: HogFlow
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface workflowsLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        workflowsFuse: (workflows: HogFlow[]) => Fuse<HogFlow>
        filteredWorkflows: (workflows: HogFlow[], filters: WorkflowsFilters, workflowsFuse: Fuse<HogFlow>) => HogFlow[]
        allArchivedSelected: (filteredWorkflows: HogFlow[], selectedArchivedWorkflowIds: Set<string>) => boolean
        selectedArchivedCount: (selectedArchivedWorkflowIds: Set<string>) => number
        creators: (workflows: HogFlow[]) => {
            email: string
            id: number
            name: string
            uuid: string
        }[]
    }
}

export type workflowsLogicType = MakeLogicType<
    workflowsLogicValues,
    workflowsLogicActions,
    Record<string, any>,
    workflowsLogicMeta
>

export const workflowsLogic = kea<workflowsLogicType>([
    key(() => 'workflowsLogic'),
    path(['products', 'workflows', 'frontend', 'workflowsLogic']),
    actions({
        toggleWorkflowStatus: (workflow: HogFlow) => ({ workflow }),
        duplicateWorkflow: (workflow: HogFlow) => ({ workflow }),
        archiveWorkflow: (workflow: HogFlow) => ({ workflow }),
        restoreWorkflow: (workflow: HogFlow) => ({ workflow }),
        deleteWorkflow: (workflow: HogFlow) => ({ workflow }),
        deleteSelectedWorkflows: true,
        loadWorkflows: () => ({}),
        setFilters: (filters: Partial<WorkflowsFilters>) => ({ filters }),
        setSearchTerm: (search: string) => ({ search }),
        setCreatedBy: (createdBy: string | null) => ({ createdBy }),
        setStatusFilter: (status: WorkflowStatusFilter) => ({ status }),
        toggleArchivedWorkflowSelection: (id: string) => ({ id }),
        selectAllArchivedWorkflows: (ids: string[]) => ({ ids }),
        clearArchivedWorkflowSelection: true,
    }),
    reducers({
        filters: [
            { search: '', createdBy: null, status: 'all' } as WorkflowsFilters,
            {
                setFilters: (state, { filters }) => ({ ...state, ...filters }),
                setSearchTerm: (state, { search }) => ({ ...state, search }),
                setCreatedBy: (state, { createdBy }) => ({ ...state, createdBy }),
                setStatusFilter: (state, { status }) => ({ ...state, status }),
            },
        ],
        selectedArchivedWorkflowIds: [
            new Set<string>(),
            {
                toggleArchivedWorkflowSelection: (state, { id }) => {
                    const next = new Set(state)
                    if (next.has(id)) {
                        next.delete(id)
                    } else {
                        next.add(id)
                    }
                    return next
                },
                selectAllArchivedWorkflows: (_, { ids }) => new Set<string>(ids),
                clearArchivedWorkflowSelection: () => new Set<string>(),
                setStatusFilter: () => new Set<string>(),
                loadWorkflowsSuccess: () => new Set<string>(),
            },
        ],
        hasLoadedWorkflows: [
            false as boolean,
            {
                loadWorkflows: () => false,
                loadWorkflowsSuccess: () => true,
            },
        ],
    }),
    loaders(({ actions, values }) => ({
        workflows: [
            [] as HogFlow[],
            {
                loadWorkflows: async () => {
                    const response = await api.hogFlows.getHogFlows()
                    return response.results
                },
                toggleWorkflowStatus: async ({ workflow }) => {
                    const updatedWorkflow = await api.hogFlows.updateHogFlow(workflow.id, {
                        status: workflow.status === 'active' ? 'draft' : 'active',
                    })
                    return values.workflows.map((c) => (c.id === updatedWorkflow.id ? updatedWorkflow : c))
                },
                duplicateWorkflow: async ({ workflow }) => {
                    const duplicatedWorkflow = await api.hogFlows.createHogFlow({
                        ...workflow,
                        status: 'draft',
                        name: `${workflow.name} (copy)`,
                    })
                    return [duplicatedWorkflow, ...values.workflows]
                },
                archiveWorkflow: async ({ workflow }) => {
                    LemonDialog.open({
                        width: 500,
                        title: 'Archive workflow?',
                        description: `Are you sure you want to archive "${workflow.name}"?${
                            workflow.status === 'active'
                                ? ' In-progress workflow invocations will end without completing.'
                                : ''
                        }`,
                        primaryButton: {
                            children: 'Archive',
                            type: 'primary',
                            status: 'danger',
                            onClick: async () => {
                                try {
                                    await api.hogFlows.updateHogFlow(workflow.id, {
                                        status: 'archived',
                                    })
                                    lemonToast.success(`Workflow "${workflow.name}" archived`)
                                    router.actions.push(urls.workflows())
                                    actions.loadWorkflows()
                                } catch (error: any) {
                                    lemonToast.error(
                                        `Failed to archive workflow: ${error.detail || error.message || 'Unknown error'}`
                                    )
                                }
                            },
                        },
                        secondaryButton: {
                            children: 'Cancel',
                        },
                    })
                    // Return unchanged workflows since dialog handles the update
                    return values.workflows
                },
                restoreWorkflow: async ({ workflow }) => {
                    try {
                        const updatedWorkflow = await api.hogFlows.updateHogFlow(workflow.id, {
                            status: 'draft',
                        })
                        lemonToast.success(`Workflow "${workflow.name}" restored to draft status`)
                        return values.workflows.map((c) => (c.id === updatedWorkflow.id ? updatedWorkflow : c))
                    } catch (error: any) {
                        lemonToast.error(
                            `Failed to restore workflow: ${error?.detail || error?.message || 'Unknown error'}`
                        )
                        return values.workflows
                    }
                },
                deleteWorkflow: async ({ workflow }) => {
                    LemonDialog.open({
                        width: 500,
                        title: 'Delete workflow?',
                        description: `Are you sure you want to permanently delete "${workflow.name}"? This action cannot be undone.`,
                        primaryButton: {
                            children: 'Delete',
                            type: 'primary',
                            status: 'danger',
                            onClick: async () => {
                                try {
                                    await api.hogFlows.deleteHogFlow(workflow.id)
                                    lemonToast.success(`Workflow "${workflow.name}" deleted`)
                                    deleteFromTree('hog_flow/', workflow.id)
                                    actions.loadWorkflows()
                                } catch (error: any) {
                                    lemonToast.error(
                                        `Failed to delete workflow: ${error.detail || error.message || 'Unknown error'}`
                                    )
                                }
                            },
                        },
                        secondaryButton: {
                            children: 'Cancel',
                        },
                    })
                    return values.workflows
                },
            },
        ],
    })),
    selectors({
        workflowsFuse: [
            (s) => [s.workflows],
            (workflows: HogFlow[]): Fuse<HogFlow> => {
                return createFuse(workflows || [], {
                    keys: [{ name: 'name', weight: 2 }, 'description'],
                    threshold: 0.3,
                    ignoreLocation: true,
                    includeMatches: true,
                })
            },
        ],
        filteredWorkflows: [
            (s) => [s.workflows, s.filters, s.workflowsFuse],
            (workflows: HogFlow[], filters: WorkflowsFilters, workflowsFuse: Fuse<HogFlow>): HogFlow[] => {
                let filtered = workflows

                if (filters.search) {
                    const searchResults = workflowsFuse.search(filters.search)
                    const matchedIds = new Set(searchResults.map((result) => result.item.id))
                    filtered = filtered.filter((w) => matchedIds.has(w.id))
                }

                if (filters.createdBy) {
                    filtered = filtered.filter((w) => w.created_by?.uuid === filters.createdBy)
                }

                if (filters.status !== 'all') {
                    filtered = filtered.filter((w) => w.status === filters.status)
                }

                return filtered
            },
        ],
        allArchivedSelected: [
            (s) => [s.filteredWorkflows, s.selectedArchivedWorkflowIds],
            (filteredWorkflows: HogFlow[], selectedIds: Set<string>): boolean => {
                return filteredWorkflows.length > 0 && filteredWorkflows.every((w) => selectedIds.has(w.id))
            },
        ],
        selectedArchivedCount: [
            (s) => [s.selectedArchivedWorkflowIds],
            (selectedIds: Set<string>): number => selectedIds.size,
        ],
        creators: [
            (s) => [s.workflows],
            (workflows: HogFlow[]) => {
                const uniqueCreators = new Map<number, { id: number; uuid: string; name: string; email: string }>()
                workflows.forEach((workflow) => {
                    if (workflow.created_by) {
                        uniqueCreators.set(workflow.created_by.id, {
                            id: workflow.created_by.id,
                            uuid: workflow.created_by.uuid,
                            name: workflow.created_by.first_name || workflow.created_by.email,
                            email: workflow.created_by.email,
                        })
                    }
                })
                return Array.from(uniqueCreators.values())
            },
        ],
    }),
    listeners(({ actions, values }) => ({
        loadWorkflowsFailure: ({ error }) => {
            lemonToast.error(`Failed to load workflows: ${error || 'Unknown error'}`)
        },
        deleteSelectedWorkflows: () => {
            const ids = Array.from(values.selectedArchivedWorkflowIds)
            if (ids.length === 0) {
                return
            }
            LemonDialog.open({
                width: 500,
                title: `Delete ${ids.length} archived workflow${ids.length === 1 ? '' : 's'}?`,
                description: `Are you sure you want to permanently delete ${ids.length} archived workflow${ids.length === 1 ? '' : 's'}? This action cannot be undone.`,
                primaryButton: {
                    children: 'Delete',
                    type: 'primary',
                    status: 'danger',
                    onClick: async () => {
                        try {
                            const result = await api.hogFlows.bulkDeleteHogFlows(ids)
                            lemonToast.success(`${result.deleted} workflow${result.deleted === 1 ? '' : 's'} deleted`)
                            for (const id of ids) {
                                deleteFromTree('hog_flow/', id)
                            }
                            actions.clearArchivedWorkflowSelection()
                            actions.loadWorkflows()
                        } catch (error: any) {
                            lemonToast.error(
                                `Failed to delete workflows: ${error?.detail || error?.message || 'Unknown error'}`
                            )
                        }
                    },
                },
                secondaryButton: {
                    children: 'Cancel',
                },
            })
        },
    })),
])
